import cls from 'classnames';
import { useEffect, useRef, useState } from 'react';

import TicketQrCode from '@/components/account/ticket-qr-code';
import Image from '@/components/image';
import getVariablePlainText from '@/lib/get-variable-plain-text';
import useVariables from '@/lib/hooks/use-variables';

const COPIED_FEEDBACK_DURATION_MS = 2000;

interface TicketCardProps {
  image?: string;
  title: string;
  modus?: string;
  status?: boolean;
  ticketType: string;
  barcode?: string;
  url?: string;
}

const TicketCard = ({
  image,
  title,
  status,
  ticketType,
  modus,
  barcode,
  url,
}: TicketCardProps) => {
  const variables = useVariables();
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const copiedTimeoutRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(copiedTimeoutRef.current), []);

  const copyCode = async () => {
    if (!barcode || isCodeCopied) {
      return;
    }

    try {
      await navigator.clipboard.writeText(barcode);
    } catch {
      // Clipboard not available: keep the default text, no feedback
      return;
    }

    setIsCodeCopied(true);
    copiedTimeoutRef.current = window.setTimeout(
      () => setIsCodeCopied(false),
      COPIED_FEEDBACK_DURATION_MS,
    );
  };

  const openTicketPage = () => {
    if (url) {
      window.dataLayer?.push({
        event: 'ticket_calvis_click',
        ticket_type: ticketType,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={cls(
        'relative overflow-hidden h-full shadow-[0_0_16px_0_rgba(18,17,19,0.50)] rounded-[16px] aspect-[2/3]',
        'w-[calc((((100vw-90px)/6)*4)+50px)] md:max-w-[400px] md:w-full',
        !image && 'ticket-placeholder-gradient',
      )}
    >
      {image && (
        <>
          <Image
            src={image}
            alt={title}
            basicLoader
            objectFit="cover"
            className="h-full w-full"
          />

          <div className="h-full w-full backdrop-blur-[32px] qr-code-card-gradient absolute top-0 left-0" />
        </>
      )}

      <div className="z-10 absolute flex flex-col justify-between items-center shrink-0 p-4 top-0 left-0 h-full w-full">
        <h4 className="text-h4 self-stretch text-center pt-1 pb-[10px]">
          {title}
        </h4>

        <div className="w-full">
          {barcode && <TicketQrCode barcode={barcode} alt={title} />}
          {barcode && (
            <div className="pt-2 pb-[2px]">
              <div className="text-small text-white text-center">{barcode}</div>
              <div
                onClick={copyCode}
                aria-live="polite"
                className={cls(
                  'text-small text-pink text-center uppercase h-4',
                  isCodeCopied ? 'cursor-default' : 'underline cursor-pointer',
                )}
              >
                {isCodeCopied ? 'Kopiert!' : 'Code kopieren'}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-body2">
              {getVariablePlainText(
                variables['account-tickets-category-text'],
              ) || 'Kategorie: '}
            </div>
            <div className="text-body2">{ticketType}</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-overline text-gray-3">Gescannt</div>
            <div className="text-overline text-gray-3">
              {status ? 'Ja' : 'Nein'}
            </div>
          </div>
          {modus && (
            <div className="grid grid-cols-2 gap-4">
              <div className="text-overline text-gray-3">Modus</div>
              <div className="text-overline text-gray-3">{modus}</div>
            </div>
          )}

          {url && (
            <div
              onClick={openTicketPage}
              className="h-8 mt-2 mb-2 text-small text-white underline flex justify-center items-center cursor-pointer"
            >
              {getVariablePlainText(
                variables['account-tickets-mobile-info-text'],
              ) || 'Weitere Informationen zum Ticket'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketCard;
